import * as React from 'react';

import { Accordion, AccordionSummary, AccordionDetails, Button, Skeleton, Typography } from '@mui/material';
import { Stack, FormControl, InputLabel, Input, FormLabel, RadioGroup, FormControlLabel, Radio, Checkbox, TextField } from '@mui/material';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { PageSection } from "../../subs/PageSection";
import Grid from '@mui/material/Grid2';
import { WorkshopProductCombination, WSProdConfigAllReady, setAllProdTestCombination, updateProdTestCombination } from '../../types/WorkshopProductCombination';
import { WSConfigProductData, WSConfigCombinationData, WSConfigCombinationIfaceData } from '../../types/WSConfigData';
import { WorkshopProduct, updateWorkshopProduct } from '../../types/WorkshopProducts';
import { WorkshopProductInterface, updateWsProdIface } from '../../types/WorkshopProductInterface';
import { WSCInputCompletionChip } from './WSCInputCompletionChip';
import { WSCiFaceAccordion } from './WSCiFaceAccordion'; 
import { WSCTestCases } from './WSCTestCases';
import { insertresultheaders } from '../../types/WorkshopResult';
import { TestSupportStatus } from '../../types/WorkshopTestCases';
import { copyArrayWithIndexChanged } from '../../Utils';
import { ButtonLink } from '../../subs/ButtonLink';
import { AlertProps } from '@mui/material/Alert';
import { useSnackbar } from '../../../contexts/SnackBarContext';
import { ImportWorkshopProductNRModal } from "../../modals/ImportWorkshopProductNRModal";

const ImportPanel = ({ wsConfigProductData, refreshWorkshopProductHandler }:
    { wsConfigProductData: WSConfigProductData | undefined, refreshWorkshopProductHandler: () => void }) => {

    const [importModalOpenState, setImportModalOpenState] = React.useState<boolean>(false);
    const [givenProductID, setGivenProductID] = React.useState<number>(0);
    const [workshopProduct, setWorkshopProduct] = React.useState<WorkshopProduct | undefined>(undefined);

    React.useEffect(() => {
        if (wsConfigProductData) {
            const wsprod: WorkshopProduct = {
                id: wsConfigProductData.workshopProductId,
                productId: wsConfigProductData.productId,
                workshopId: wsConfigProductData.workshopId,
                status: wsConfigProductData.status,
                productVersion: wsConfigProductData.productVersion,
                releaseStatus: wsConfigProductData.releaseStatus,
                description: wsConfigProductData.description,
                // isServer & isClient & ready to test are moved to combination level and no longer relevant here
                isServer: false,
                isClient: false,
                clientReadyToTest: false,
                serverReadyToTest: false,
            };

            setWorkshopProduct(wsprod);
        }
        else setWorkshopProduct(undefined);
    }, [wsConfigProductData]);

    return <>
        {workshopProduct && <>
            <ImportWorkshopProductNRModal workshopId={workshopProduct.workshopId} currentWorkshopProductId={workshopProduct.id} givenProductID={givenProductID}
                refreshWorkshopProductHandler={refreshWorkshopProductHandler} openState={importModalOpenState} setOpenState={setImportModalOpenState} />

            <PageSection heading='Import settings from another instance:'>
                <Button startIcon={<ImportExportIcon />} onClick={() => { setGivenProductID(workshopProduct.productId); setImportModalOpenState(true) }} >from previous Workshops</Button>
                <Button startIcon={<ImportExportIcon />} onClick={() => { setGivenProductID(0); setImportModalOpenState(true) }}>from another Product</Button>
            </PageSection> </>}
        </>
}

export const WSConfigServer = ({ wsConfigProductData, disabled, isGlobalAdminUser, setWsConfigProductData, refreshProductData }:
    {
        wsConfigProductData: WSConfigProductData | undefined,
        disabled: boolean,
        isGlobalAdminUser: boolean,
        setWsConfigProductData: React.Dispatch<React.SetStateAction<WSConfigProductData | undefined>>,
        refreshProductData: (newlyMarkedTestCases?: number) => void
    }) => {
    const { showSnackbar } = useSnackbar();
    const [wp, setWp] = React.useState(wsConfigProductData);
    const [wpFormComplete, setWpFormComplete] = React.useState<boolean>(false);
    const [allReady, setAllReady] = React.useState<string | undefined>(undefined);
    const [combinationIfaces, setCombinationIfaces] = React.useState<WSConfigCombinationIfaceData[]>([]);
    const [wpdescription, setWpDescription] = React.useState('');
    const [wpproductVersion, setWpProductVersion] = React.useState('');

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        if (wsConfigProductData === undefined) return;
        let formComplete = false;
        if (wsConfigProductData.productVersion && wsConfigProductData.productVersion !== ''
        //    && wsConfigProductData.description && wsConfigProductData.description !== ''
        ) {
            formComplete = true;
            wsConfigProductData.combinationIfaces.forEach(wpiface => {
                if (wpiface.supportedByProduct) {
                    wpiface.testCaseGroups.forEach(wptcg => {
                        wptcg.testCases.forEach(tc => {
                            if (tc.supportStatus === TestSupportStatus.Undecided) {
                                formComplete = false;
                                return;
                            }
                        });
                        if (!formComplete) return;
                    });
                }
                if (!formComplete) return;
            });
        }
        setWpFormComplete(formComplete);
        setWp(wsConfigProductData);
    }, [wsConfigProductData]);

    React.useEffect(() => {
        setWpDescription(wp?.description ?? '');
        setWpProductVersion(wp?.productVersion ?? '');
        setCombinationIfaces(wp?.combinationIfaces ?? []);
    }, [wp]);

    React.useEffect(() => {
        if (allReady === undefined) return;     // if the user makes a selection MUI will force the radio to change. Here we want to force MUI to empty the control again.
        setAllReady(undefined);
    }, [allReady]);

    const workshopProductSubmitHandler = async (wsProduct: WorkshopProduct ) => {
        updateWorkshopProduct(wsProduct, new AbortController()).then(response => {
            if (response.failed) {
                showUserFeedback(response.errorText);
                return;
            }
            const updatedProduct = response.result;
            let wpLoc = wp;

            if (wpLoc) {
                wpLoc.description = updatedProduct.description ?? '';
                wpLoc.productVersion = updatedProduct.productVersion ?? '';

                setWsConfigProductData(wpLoc);
            }
            showUserFeedback("Product Information Saved Successfully.", 'success');
        }).catch(error => {
            showUserFeedback(error.message);
        });
    }

    const OnReadyToTestChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!wp) return;
        const combinationData = wp.combinationData.find(cd => cd.testCombinationId === wp.key.testCombinationId);
        if (!combinationData) return;

        let allowChange = true;

        if (event.target.value === 'true') {
            wp.combinationIfaces.forEach(wpi => {
                if (!wpi.supportedByProduct) return;
                wpi.testCaseGroups.forEach(wptcg => {
                    if (wptcg.testCases.find(tc => tc.supportStatus === TestSupportStatus.Undecided) !== undefined) {
                        showUserFeedback('Please mark all test cases as supported or unsupported, before making the Product ready for testing.');
                        allowChange = false;
                        return;
                    }
                });
                if (!allowChange) return;
            });
        }
        else if (event.target.value === 'reset') {
            const updatedCombination: WorkshopProductCombination = {
                id: wp.workshopProductCombinationId,
                testCombinationId: wp.key.testCombinationId,
                workshopProductId: wp.key.workshopProductId,
                isProvider: combinationData.isProvider,
                isConsumer: combinationData.isConsumer,
                providerReadyToTest: combinationData.providerReadyToTest,
                consumerReadyToTest: combinationData.consumerReadyToTest
            };
            insertResultHeadersHandler(updatedCombination, undefined);
            return;
        }

        if (!allowChange) return;

        const updatedCombination: WorkshopProductCombination = {
            id: wp.workshopProductCombinationId,
            testCombinationId: wp.key.testCombinationId,
            workshopProductId: wp.key.workshopProductId,
            isProvider: combinationData.isProvider,
            isConsumer: combinationData.isConsumer,
            providerReadyToTest: wp.key.wsTreeEntryType !== 1 ? combinationData.providerReadyToTest : event.target.value === 'true',
            consumerReadyToTest: wp.key.wsTreeEntryType !== 2 ? combinationData.consumerReadyToTest : event.target.value === 'true'
        };

        let wpLoc = { ...wp };
        wpLoc.key.wsTreeEntryType === 1 ? combinationData.providerReadyToTest = event.target.value === 'true' : combinationData.consumerReadyToTest = event.target.value === 'true';
        wpLoc.readyToTest = event.target.value === 'true';
        wpLoc.combinationData = copyArrayWithIndexChanged(wpLoc.combinationData, wpLoc.combinationData?.findIndex(cd => cd.testCombinationId === combinationData.testCombinationId) ?? -1, combinationData);
        setWp(wpLoc);

        updateProdTestCombination(updatedCombination, new AbortController()).then((response) => {
            if (response.failed) {
                showUserFeedback(response.errorText);
            }
            refreshProductData(0);
        }).catch(error => {
            showUserFeedback(error.message);
            refreshProductData(0);
        });
    }

    const OnReadyToTestChangeAllHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!wp) return;
        let wsProdConfigAllReady: WSProdConfigAllReady = {
            workshopProductId: wp.workshopProductId ?? 0,
            setReadyToTest: event.target.value === 'true'
        }
        setAllReadyToTest(wsProdConfigAllReady).catch(error => {
            showUserFeedback(error.message);
            refreshProductData(0);
        });
        setAllReady(event.target.value);        // set the value to trigger the useEffect to reset the radio button again
    }

    const setAllReadyToTest = async (wsProdConfigAllReady: WSProdConfigAllReady) => {
        if (!wp) return;

        const response = await setAllProdTestCombination(wsProdConfigAllReady, new AbortController());

        if (response.failed) {
            showUserFeedback(response.errorText);
            refreshProductData(0);
            return;
        }

        const updatedCombinations = response.result;
        let wpLoc = { ...wp, key: { ...wp.key }, combinationData: [...(wp.combinationData ?? [])] };
        let errorNotice: string = '';

        updatedCombinations.forEach(updatedCombination => {
            if (wpLoc.key.testCombinationId === updatedCombination.testCombinationId) {
                wpLoc.readyToTest = wpLoc.key.wsTreeEntryType === 1 ? updatedCombination.providerReadyToTest : updatedCombination.consumerReadyToTest;
            }

            const existingCombinationData = wpLoc.combinationData?.find(wpc => wpc.testCombinationId === updatedCombination.testCombinationId);
            let updatedCombinationData: WSConfigCombinationData | undefined = existingCombinationData ? { ...existingCombinationData } : undefined;
            if (updatedCombinationData) {
                updatedCombinationData.consumerReadyToTest = updatedCombination.consumerReadyToTest;
                updatedCombinationData.providerReadyToTest = updatedCombination.providerReadyToTest;
                wpLoc.combinationData = copyArrayWithIndexChanged(wpLoc.combinationData,
                    wpLoc.combinationData?.findIndex(wpc => wpc.testCombinationId === updatedCombination.testCombinationId) ?? -1, updatedCombinationData);
            }
            if (updatedCombination.updateErrorReason !== undefined && updatedCombination.updateErrorReason !== null && updatedCombination.updateErrorReason !== '') {
                errorNotice += `Combination ${updatedCombinationData?.combinationName ?? updatedCombination.testCombinationId}: ${updatedCombination.updateErrorReason}\n`;
            }
        });

        setWp(wpLoc);

        if (errorNotice !== '') showUserFeedback(errorNotice);
        refreshProductData(0);
    }

    // rebuild the result headers if the counters are wrong...
    const insertResultHeadersHandler = async (updatedCombination: WorkshopProductCombination, combinationData: WSConfigCombinationData | undefined) => {
        // insert the Results header for this workshop product and all combinations of interfaces, if it is set to ready to test
        insertresultheaders(updatedCombination, new AbortController()).then(response => {
            if (response.failed) {
                showUserFeedback(`Failed to update test results for combination ${combinationData?.combinationName ?? updatedCombination.testCombinationId}: ${response.errorText}`);
                return;
            }
        }).catch(error => {
            showUserFeedback(`Failed to update test results for combination ${combinationData?.combinationName ?? updatedCombination.testCombinationId}: ${error.message}`);
        });
    }

    const wpIfaceSubmitHandler = async (wpIfaces: WSConfigCombinationIfaceData) => {
        if (!wp || !wpIfaces) return;

        const updateIface: WorkshopProductInterface = {
            id: wpIfaces.wPIfaceID,
            interfaceId: wpIfaces.interfaceId,
            supportedByProduct: wpIfaces.supportedByProduct,
            serverEndpoint: wpIfaces.serverEndpoint,
            workshopProductId: wp.workshopProductId ?? 0
        };

        updateWsProdIface(updateIface, new AbortController()).then(response => {
            if (response.failed) {
                showUserFeedback(response.errorText);
                return;
            }

            let wpLoc = wp;
            let updatedInterface = response.result;
            let index = wpLoc.combinationIfaces.findIndex(wpi => wpi.wPIfaceID === updatedInterface.id);
            if (index !== -1) {
                wpLoc.combinationIfaces = copyArrayWithIndexChanged(wpLoc.combinationIfaces, index, { ...wpIfaces, serverEndpoint: updatedInterface.serverEndpoint ?? '' });
            }
            setWp(wpLoc);
        }).catch(error => {
            showUserFeedback(error.message);
            return;
        });

        showUserFeedback("Product interface support updated.", "info");
    }

    return <>
        <ImportPanel wsConfigProductData={wp} refreshWorkshopProductHandler={refreshProductData} />
        <Stack rowGap={20}>
        {wp ?
            <>
                <Accordion defaultExpanded={true}>
                    <AccordionSummary>
                        <Stack width={'100%'} direction={'row'} justifyContent={'space-between'}>
                            <Typography variant='bold'>{`${wp.combinationTypeName} information: ${wp.combinationName}`}</Typography>
                                {<WSCInputCompletionChip completed={wpFormComplete} />}
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                        {<form id='TestingPairServer'>
                            <fieldset disabled={disabled} className={'disabledForm'}>
                                <Stack direction={'column'} rowGap={30}>
                                    <Grid container rowGap={20} columnSpacing={20}>
                                        <Grid size={{ xs: 4 }}>
                                            <FormControl required fullWidth>
                                                <InputLabel>{"Product name"}</InputLabel>
                                                <Input value={wp.productName ?? ""} disabled />
                                            </FormControl>
                                        </Grid>
                                        <Grid size={{ xs: 4 }}>
                                            <FormControl required fullWidth >
                                                <InputLabel>{"Product version"}</InputLabel>
                                                <Input value={wpproductVersion} 
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                    onChange={(e) => setWpProductVersion(e.target.value)}
                                                    onBlur={(e) => workshopProductSubmitHandler({
                                                        id: wp.workshopProductId,
                                                        productId: wp.productId,
                                                        workshopId: wp.workshopId,
                                                        status: wp.status,
                                                        releaseStatus: wp.releaseStatus,
                                                        isServer: false,
                                                        isClient: false,
                                                        clientReadyToTest: false,
                                                        serverReadyToTest: false,
                                                        productVersion: e.target.value,
                                                        description: wp.description
                                                    })} />
                                            </FormControl>
                                        </Grid>
                                        <Grid size={{ xs: 4 }}>
                                            <FormControl required>
                                                <FormLabel>{`${wp.combinationTypeName} ready to test?`}</FormLabel>
                                                <RadioGroup name='readytoTestRadioButtons' row value={wp.readyToTest ? 'true' : 'false'} onChange={OnReadyToTestChangeHandler}>
                                                    <FormControlLabel value={'true'} control={<Radio />} label={"Yes"} />
                                                    <FormControlLabel value={'false'} control={<Radio />} label={"No"} />
                                                    {isGlobalAdminUser && <FormControlLabel value='reset' control={<Radio />} label={"Recalculate"} />}
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>

                                        <Grid container size={{ xs: 12 }}>
                                            <Grid container size={{ xs: 5 }} >
                                                <FormControl required>
                                                    <FormLabel>{"Supported Interfaces"}</FormLabel>
                                                    {wp.combinationIfaces && wp.combinationIfaces.map(wi => {
                                                        return <Grid container size={{ xs: 12 }}>
                                                        <FormControlLabel control={<Checkbox checked={wi.supportedByProduct ? true : false} disabled/>}
                                                            label={wi.interfaceName} />
                                                        </Grid>;
                                                    })}
                                                </FormControl>
                                            </Grid>
                                            <Grid container size={{ xs: 7 }} >
                                                {(disabled || allReady !== undefined) ? null :  // for some reason MUI turns the text white after selection and won'T unset the radio value, this workaround prevents that
                                                    <FormControl>
                                                        <Grid container rowGap={2} columnSpacing={5}>
                                                            <Grid container size={{ xs: 8 }}>
                                                                <FormLabel sx={{ alignContent: 'center' }}>{'Set all to Ready:'}</FormLabel>
                                                            </Grid>
                                                            <Grid container size={{ xs: 4 }}>
                                                                <RadioGroup name={'displayProviderReadyALL'} row onChange={OnReadyToTestChangeAllHandler} value={''} >
                                                                    <FormControlLabel value={'true'} control={<Radio />} label={"Yes"} />
                                                                    <FormControlLabel value={'false'} control={<Radio />} label={"No"} />
                                                                </RadioGroup>
                                                            </Grid>
                                                            <Grid container size={{ xs: 4 }} borderBottom={1} borderTop={1}>
                                                                <FormLabel >{"Scenario"}</FormLabel>
                                                            </Grid>
                                                            <Grid container size={{ xs: 4 }} borderBottom={1} borderTop={1}>
                                                                <FormLabel>{"Consumer Ready"}</FormLabel>
                                                            </Grid>
                                                            <Grid container size={{ xs: 4 }} borderBottom={1} borderTop={1}>
                                                                <FormLabel>{"Provider Ready"}</FormLabel>
                                                            </Grid>
                                                            {wp.combinationData && wp.combinationData.map(wpc => {
                                                                return <>
                                                                    <Grid container size={{ xs: 4 }}>
                                                                        <FormLabel sx={{ alignContent: 'center' }}>{wpc.combinationName}</FormLabel>
                                                                    </Grid>
                                                                    <Grid container size={{ xs: 4 }}>
                                                                        {wpc.isConsumer &&
                                                                            <RadioGroup name={'displayConsumerReady' + wpc.testCombinationId} row value={wpc.consumerReadyToTest}>
                                                                                <FormControlLabel value={'true'} control={<Radio />} label={"Yes"} disabled />
                                                                                <FormControlLabel value={'false'} control={<Radio />} label={"No"} disabled />
                                                                            </RadioGroup>}
                                                                    </Grid>
                                                                    <Grid container size={{ xs: 4 }}>
                                                                        {wpc.isProvider &&
                                                                            <RadioGroup name={'displayProviderReady' + wpc.testCombinationId} row value={wpc.providerReadyToTest} >
                                                                                <FormControlLabel value={'true'} control={<Radio />} label={"Yes"} disabled />
                                                                                <FormControlLabel value={'false'} control={<Radio />} label={"No"} disabled />
                                                                            </RadioGroup>}
                                                                    </Grid>
                                                                </>;
                                                            })}
                                                        </Grid>
                                                    </FormControl>
                                                }
                                            </Grid>
                                            <Grid container size={{ xs: 12 }} paddingBottom={20}>
                                                <Grid container size={{ xs: 6 }}>
                                                        <ButtonLink key={`Start${wp.workshopId}`} to={`/ViewUserProducts/${wp.workshopId}`} >
                                                        Edit Products
                                                    </ButtonLink>
                                                </Grid>
                                                <Grid container size={{ xs: 6 }}>

                                                </Grid>
                                            </Grid>
                                            {wp.key.wsTreeEntryType === 1 && 
                                                <Grid container size={{ xs: 12 }}>
                                                    <FormControl fullWidth>
                                                        <FormLabel>{"ServerProgID / URL"}</FormLabel>
                                                        {combinationIfaces && combinationIfaces.map((wi, index) => {
                                                            return <Grid height={'100%'}>
                                                                <TextField size='small' fullWidth
                                                                    disabled={!wi.supportedByProduct}
                                                                    value={wi.serverEndpoint}
                                                                    onChange={(e) => setCombinationIfaces(copyArrayWithIndexChanged(combinationIfaces, index, { ...wi, serverEndpoint: e.target.value }))}
                                                                    onBlur={(e) => {
                                                                        wpIfaceSubmitHandler({ ...wi, serverEndpoint: e.target.value });
                                                                    }} />
                                                            </Grid>
                                                        })}
                                                    </FormControl>
                                                </Grid>}
                                        </Grid>

                                        <Grid size={{ xs: 12 }}>
                                            <FormControl fullWidth>
                                                <TextField label={"Product description"} variant='outlined' multiline minRows={7} maxRows={7} fullWidth
                                                    value={wpdescription}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                    onChange={(e) => setWpDescription(e.target.value)}
                                                    onBlur={(e) => workshopProductSubmitHandler({
                                                        id: wp.workshopProductId,
                                                        productId: wp.productId,
                                                        workshopId: wp.workshopId,
                                                        status: wp.status,
                                                        releaseStatus: wp.releaseStatus,
                                                        isServer: false,
                                                        isClient: false,
                                                        clientReadyToTest: false,
                                                        serverReadyToTest: false,
                                                        description: e.target.value,
                                                        productVersion: wp.productVersion
                                                    })} />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Stack>
                            </fieldset>
                        </form>}
                    </AccordionDetails>
                </Accordion>
                <Typography variant='h5'>{"Specific Server characteristics or optional features."}</Typography>
                {wp.key.wsTreeEntryType === 1 && <WSCiFaceAccordion ifaceDataIn={wp.combinationIfaces} wsConfigProductData={wp}
                    refreshProductData={refreshProductData} setWsConfigProductData={setWsConfigProductData} />}
                <WSCTestCases ifaceDataIn={wp.combinationIfaces} wsConfigProductData={wp}
                    refreshProductData={refreshProductData} />
            </>
            :
            <Skeleton variant="rounded" width={'90%'} height={'50%'} />
        }
        </Stack>
    </>
}
